import React from 'react';
import { useIntl } from '../../util/reactIntl';
import { Heading, Icons } from '../../components';
import { AMENITIES_OPTIONS } from '../../util/enums';
import { getAmenityIconSrc } from '../../util/amenityIcons';

import css from './ListingPage.module.css';

export const AMENITY_PUBLIC_DATA_KEYS = ['amenities', 'amenity', 'features'];

const humanizeAmenityKey = key =>
  `${key}`
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/^./, char => char.toUpperCase());

const cleanAmenityLabel = label => `${label}`.replace(/\s*\(specify in description\)/gi, '').trim();

const normalizeOption = option => {
  const key = option?.key ?? option?.option ?? option?.value ?? option?.id;
  if (key == null) {
    return null;
  }

  return {
    key: `${key}`,
    label: cleanAmenityLabel(option?.label || option?.name || humanizeAmenityKey(key)),
  };
};

const addOption = (optionsByKey, option) => {
  const normalized = normalizeOption(option);
  if (normalized) {
    optionsByKey.set(normalized.key, normalized);
  }
};

const normalizeAmenityValue = value => {
  if (!value) {
    return [];
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map(v => v.trim())
      .filter(Boolean)
      .map(key => ({ key }));
  }

  if (Array.isArray(value)) {
    return value
      .map(item => {
        if (item == null) {
          return null;
        }
        if (typeof item === 'string' || typeof item === 'number') {
          return { key: `${item}` };
        }

        const key = item.key ?? item.option ?? item.value ?? item.id ?? item.name;
        return key == null
          ? null
          : {
              key: `${key}`,
              label: item.label || item.name,
            };
      })
      .filter(Boolean);
  }

  if (typeof value === 'object') {
    return Object.entries(value)
      .filter(([, selected]) => Boolean(selected))
      .map(([key, selected]) => ({
        key,
        label: typeof selected === 'object' ? selected?.label || selected?.name : null,
      }));
  }

  return [];
};

export const normalizeAmenities = publicData => {
  const selectedByKey = new Map();

  AMENITY_PUBLIC_DATA_KEYS.forEach(publicDataKey => {
    normalizeAmenityValue(publicData?.[publicDataKey]).forEach(item => {
      if (item?.key && !selectedByKey.has(item.key)) {
        selectedByKey.set(item.key, item);
      }
    });
  });

  return Array.from(selectedByKey.values());
};

export const getAmenityOptions = (listingFieldConfigs = [], selectedAmenities = []) => {
  const optionsByKey = new Map();
  const configuredAmenityField = listingFieldConfigs.find(fieldConfig =>
    AMENITY_PUBLIC_DATA_KEYS.includes(fieldConfig?.key)
  );

  AMENITIES_OPTIONS.forEach(addOption.bind(null, optionsByKey));
  configuredAmenityField?.enumOptions?.forEach(addOption.bind(null, optionsByKey));
  selectedAmenities.forEach(item => {
    addOption(optionsByKey, {
      key: item.key,
      label: item.label || optionsByKey.get(item.key)?.label || humanizeAmenityKey(item.key),
    });
  });

  return Array.from(optionsByKey.values());
};

const SectionAmenitiesMaybe = props => {
  const intl = useIntl();
  const { publicData, listingFieldConfigs = [] } = props;
  const selectedAmenities = normalizeAmenities(publicData);
  const selectedAmenityKeys = selectedAmenities.map(item => item.key);

  if (selectedAmenityKeys.length === 0) {
    return null;
  }

  const options = getAmenityOptions(listingFieldConfigs, selectedAmenities).filter(option =>
    selectedAmenityKeys.includes(option.key)
  );
  const heading = intl.formatMessage({ id: 'EditListingDetailsForm.amenitiesHeading' });

  return (
    <section className={`${css.sectionMultiEnum} ${css.sectionAmenities}`}>
      <Heading as="h2" rootClassName={css.sectionHeading}>
        {heading}
      </Heading>
      <ul className={css.amenitiesList}>
        {options.map(option => {
          const amenityIconSrc = getAmenityIconSrc(option.key);

          return (
            <li key={`ListingPage.amenities.${option.key}`} className={css.amenityItem}>
              <span className={css.amenityIcon}>
                {amenityIconSrc ? (
                  <span
                    className={css.amenityIconImage}
                    style={{ '--amenityIconUrl': `url(${amenityIconSrc})` }}
                  />
                ) : (
                  <Icons name={option.key} />
                )}
              </span>
              <span>{option.label}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default SectionAmenitiesMaybe;
